import { useEffect, useState } from "react";
import { CreditCard, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur.toUpperCase() }).format(v);
    } catch {
        return `${v} ${cur}`;
    }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

export default function InvoicesPage() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        api.get("/payments/history")
            .then((r) => setHistory(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
        // Run once on mount — all refs are stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadInvoice = async (sessionId) => {
        if (!sessionId) return;
        setDownloading(sessionId);
        try {
            const token = localStorage.getItem("tk_token");
            const res = await fetch(`${API}/payments/${sessionId}/invoice`, {
                method: "GET",
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!res.ok) {
                let msg = `Erreur ${res.status}`;
                try {
                    const j = await res.json();
                    msg = j?.detail || msg;
                } catch (parseErr) {
                    console.debug("invoice error response not JSON", parseErr);
                }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const cd = res.headers.get("Content-Disposition") || "";
            const m = cd.match(/filename="?([^";]+)"?/i);
            const filename = m?.[1] || `ticketify-facture-${sessionId.slice(-12)}.pdf`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Facture téléchargée");
        } catch (e) {
            toast.error(e.message || "Échec du téléchargement");
        } finally {
            setDownloading(null);
        }
    };

    return (
        <div className="space-y-8">
            <header>
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Factures et plans</h2>
                <p className="mt-1 text-sm text-[#86868B]">
                    Historique de vos transactions Ticketify. Téléchargez la facture PDF de chaque paiement.
                </p>
            </header>

            {loading ? (
                <div className="tk-card p-10 text-center text-[#86868B]">Chargement…</div>
            ) : history.length === 0 ? (
                <div className="tk-card p-12 text-center" data-testid="invoices-empty">
                    <CreditCard className="w-10 h-10 text-[#AEAEB2] mx-auto mb-3" />
                    <p className="text-[#86868B]">Aucune transaction pour le moment.</p>
                    <p className="mt-1 text-xs text-[#86868B]">
                        Vos paiements Ticketify apparaîtront ici dès votre premier abonnement.
                    </p>
                </div>
            ) : (
                <div className="tk-card overflow-hidden">
                    <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">
                            {history.length} transaction{history.length > 1 ? "s" : ""}
                        </p>
                        <p className="text-xs text-[#86868B]">
                            Les factures PDF sont conformes aux exigences comptables françaises.
                        </p>
                    </div>
                    {history.map((p, i) => {
                        const paid = p.payment_status === "paid";
                        const isDownloading = downloading === p.session_id;
                        return (
                            <div
                                key={p.session_id || i}
                                className={`flex items-center gap-4 p-5 ${i > 0 ? "border-t border-black/5" : ""}`}
                                data-testid={`invoice-row-${i}`}
                            >
                                <div className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center">
                                    <CreditCard className="w-5 h-5 text-[#1D1D1F]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-[#1D1D1F] capitalize">
                                        Plan {p.plan_id}
                                    </p>
                                    <p className="text-xs text-[#86868B] truncate">
                                        {fmtDate(p.created_at)}
                                        {p.session_id && ` · ${p.session_id.slice(0, 16)}…`}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold text-[#1D1D1F]">
                                        {fmtMoney(p.amount, p.currency || "EUR")}
                                    </p>
                                    <p
                                        className={`text-xs capitalize ${paid ? "text-[#34C759]" : "text-[#86868B]"}`}
                                    >
                                        {paid ? "Payée" : p.payment_status || "En attente"}
                                    </p>
                                </div>
                                <button
                                    onClick={() => downloadInvoice(p.session_id)}
                                    disabled={!p.session_id || isDownloading}
                                    title="Télécharger la facture PDF"
                                    data-testid={`invoice-download-${i}`}
                                    className="ml-2 w-10 h-10 rounded-full bg-[#F5F5F7] hover:bg-[#1D1D1F] hover:text-white text-[#1D1D1F] flex items-center justify-center transition disabled:opacity-50"
                                >
                                    {isDownloading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
